"use client";
import { useRef, useEffect, useState } from "react";
import { Capability } from "./prompt-types";
import { Card, CardContent } from "@/components/ui/card";
import CapabilityPill from "./capability-pill";
import CodeExample from "./code-example";

type PointData = { x: number; y: number }[];

export default function InferencePointing({
  image,
  result,
  query,
  duration,
  imageFile,
}: {
  image: string;
  result: PointData;
  query: string;
  duration?: number;
  imageFile?: File;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;

    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;
      const MAX_DIMENSION = 512;
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;

      // Let Tailwind handle responsive sizing, just set aspect ratio
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.scale(2, 2);

      if (
        scaledDimensions.width !== scaledWidth ||
        scaledDimensions.height !== scaledHeight
      ) {
        setScaledDimensions({ width: scaledWidth, height: scaledHeight });
      }

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

      const points = Array.isArray(result) ? (result as PointData) : [];

      if (points.length > 0) {
        const pointColor = "rgb(40, 141, 255)";
        const transparentColor = "rgba(40, 141, 255, 0.3)";

        points.forEach((point) => {
          const x = point.x * scaledWidth;
          const y = point.y * scaledHeight;
          const outerRadius = 12;
          const middleRadius = 12;
          const innerRadius = 3;

          ctx.beginPath();
          ctx.arc(x, y, outerRadius, 0, 2 * Math.PI);
          ctx.strokeStyle = pointColor;
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(x, y, middleRadius, 0, 2 * Math.PI);
          ctx.fillStyle = transparentColor;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(x, y, innerRadius, 0, 2 * Math.PI);
          ctx.fillStyle = pointColor;
          ctx.fill();
        });
      }
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, result, scaledDimensions.width, scaledDimensions.height]);

  return (
    <Card>
      <CardContent className="mt-6">
        <div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-full sm:w-auto">
              <canvas
                ref={canvasRef}
                className="rounded-lg w-full max-w-full sm:w-auto sm:max-w-md h-auto"
              />
            </div>

            <div className="flex flex-col gap-2 min-w-0 flex-grow">
              <div>
                <CapabilityPill capability={Capability.Pointing} />
              </div>
              <div>
                <div className="text-xs font-black text-muted-foreground">
                  PROMPT
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">{query.trimStart()}</div>
              </div>

              {Array.isArray(result) && result.length === 0 && (
                <div className="mt-2 text-sm text-amber-500">
                  &ldquo;No points identified in the image.&rdquo;
                </div>
              )}
              {duration && (
                <div className="text-xs text-muted-foreground mt-2">
                  Response time: {duration.toFixed(0)}ms
                </div>
              )}
            </div>
          </div>
        </div>
        {imageFile && (
          <CodeExample
            capability={Capability.Pointing}
            prompt={query}
            imageFile={imageFile}
          />
        )}
      </CardContent>
    </Card>
  );
}
